// Copyright (c) 2026 Goffy. All Rights Reserved.

import { useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import { t } from '@/i18n';
import appColors from '@/theme/colors';
import { useUpcomingRounds } from './useUpcomingRounds';
import { formatTeeTime, rsvpStatusLabel } from './format';

type Props = {
  userId: string | null | undefined;
  token: string | null | undefined;
};

/**
 * Top-of-screen list of the player's upcoming scheduled rounds. Shared by the native and web
 * my-tours screens. Tapping a row opens that round's RSVP screen. Self-refreshing: the underlying
 * hook refetches on round.scheduleUpdated / round.rsvpUpdated frames.
 */
export default function UpcomingRoundsSection({ userId, token }: Props) {
  const router = useRouter();
  const { rounds } = useUpcomingRounds({ userId, token });

  const comingCounts = useMemo(
    () =>
      rounds.reduce<Record<string, number>>((acc, round) => {
        acc[round.roundId] = round.responders.filter((r) => r.rsvpStatus === 'accepted').length;
        return acc;
      }, {}),
    [rounds]
  );

  // Hidden entirely until there is something to show, so it never adds an empty card to my-tours.
  if (!userId || !token) return null;
  if (rounds.length === 0) return null;

  return (
    <View style={styles.section} testID="upcoming-rounds-section">
      <Text style={styles.sectionTitle}>{t('schedule.upcomingTitle')}</Text>
      {
        rounds.map((round) => (
          <Pressable
            key={round.roundId}
            style={styles.row}
            onPress={() => router.push(`/round/${round.roundId}/rsvp`)}
            accessibilityLabel={round.courseName ?? t('schedule.upcomingTitle')}
            testID={`upcoming-round-${round.roundId}`}
          >
            <View style={styles.rowText}>
              <Text style={styles.courseName}>{round.courseName ?? t('scorecard.selectRoundFallback')}</Text>
              <Text style={styles.teeTime}>{formatTeeTime(round.teeTimeUtc)}</Text>
            </View>
            <View style={styles.rowMeta}>
              <Text style={styles.myStatus}>{rsvpStatusLabel(round.myRsvpStatus)}</Text>
              <Text style={styles.comingCount}>{t('rsvp.comingCount', { count: comingCounts[round.roundId] ?? 0 })}</Text>
            </View>
          </Pressable>
        ))
      }
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    width: '100%',
    borderRadius: 16,
    padding: 14,
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#1A3A20',
    gap: 8,
    marginBottom: 8,
  },
  sectionTitle: {
    color: '#A1D493',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 10,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 12,
    backgroundColor: '#0B2A14',
    borderWidth: 1,
    borderColor: '#1A3A20',
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  courseName: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 15,
  },
  teeTime: {
    color: '#A1D493',
    fontSize: 12,
    marginTop: 2,
  },
  rowMeta: {
    alignItems: 'flex-end',
  },
  myStatus: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  comingCount: {
    color: '#7FA978',
    fontSize: 11,
    marginTop: 2,
  },
});
